import { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useParams, useSearchParams } from 'react-router-dom'
import {
   AdminPanelContainer,
   Button,
   DataTotal,
   DateInput,
   DropDownList,
   FiltersWrapper,
   FlexContainer,
   Header,
   HeaderWrapper,
   Pagination,
   SearchBar,
   SubPagesBar,
   Table,
   TColumnTable,
   TSection,
   TSort
} from '../../../../components'

import { api } from '../../../../config'
import { useLocalization, useTypedSelector } from '../../../../hooks'
import {
   categoryActions,
   getMainProductSelector,
   productActions
} from '../../../../store'
import { getMainCategorySelector } from '../../../../store/category'
import { PAGE_LIMIT, RESPONSE } from './consts'

import { Image } from '../../styled'

import { useNavigate } from 'react-router-dom'
import { ELocales } from '../../../../enums'
import Moment from 'react-moment'
import axios from 'axios'
import { utils } from '../../utils'
import { TCategory, TProduct } from 'store/product/types'

const IndexPage: FC = () => {
   const { t } = useTranslation()
   const dispatch = useDispatch()
   const navigate = useNavigate()
   const {
      products,
      filters,
      loading: productLoading,
      response: productsResponse
   } = useTypedSelector(getMainProductSelector)
   const { categories } = useTypedSelector(getMainCategorySelector)

   const [page, setPage] = useState<number>(0)

   const [searchParams, setSearchParams] = useSearchParams()

   const [locale] = useLocalization()

   const [searchForm, setSearchForm] = useState({
      query: filters ? filters.query : '',
      date_start: filters ? filters.dateStart : '',
      date_end: filters ? filters.dateEnd : ''
   })

   const [filterForm, setFilterForm] = useState<{ value: string }>({
      value: filters?.categoryId ? filters.categoryId : ''
   })

   const [sortParamsForm, setSortParamsForm] = useState({
      sortBy: '',
      order: '' as TSort
   })

   const [isClicked, setIsClicked] = useState(false)

   const tableSortBy = useMemo(() => {
      {
         if (!sortParamsForm.order) {
            return { id: null, desc: false }
         }
         return { id: sortParamsForm.sortBy, desc: sortParamsForm.order > 0 }
      }
   }, [sortParamsForm])

   const [selectedItems, setSelectedItems] = useState<number[]>([])
   const [removeButtonState, setRemoveButtonState] = useState<boolean>(false)
   const [changeVisabilityState, setVisabilityChangeState] = useState<boolean>(false)

   const data = useMemo(
      () =>
         products?.data.map((product: TProduct) => {
            return {
               barcode: <>{product.barcode}</>,
               photo: <Image src={`${api.preview}${product.preview}`} />,
               label: <>{product.description && product.description?.title}</>,
               createdAt: (
                  <Moment format="DD.MM.YYYY">
                     {new Date(product?.createdAt as string)}
                  </Moment>
               ),
               category: <>{product.category.title}</>,
               total: <>{product?.amount}</>,
               price: <>{product.price}</>,
               sellStatus: <>{product.amount ? t('available') : t('notavailable')}</>
            }
         }),
      [products]
   )

   const Events = {
      onPageChangeHandler: ({ selected }: { selected: number }) => {
         setPage(selected)
      },
      postGoodsHandler: () => {
         axios.get('https://1c-api.elem.az/UT_TestAPI/hs/app_post/goods')
      },
      postAllGoodsHandler: () => {
         axios.get('https://1c-api.elem.az/UT_TestAPI/hs/app_post/goods_all')
      },

      loadOptions: async (search: string, loadOptions: any, additions: any) => {
         const page = additions?.page || 0

         const options = await Requests.getCategories()
      },
      onChangeHandler: (e: React.SyntheticEvent) => {
         const input = e.target as HTMLInputElement
         setSearchForm({ ...searchForm, [input.name]: input.value })
      },
      onSubmitSearchHandler: (e: SyntheticEvent) => {
         e.preventDefault()
         Requests.getProducts()
      },
      paginateDropDownChangeHandler: (e: SyntheticEvent) => {
         const { name, value } = e.target as HTMLInputElement

         setFilterForm((form) => ({ ...form, value: value }))
      },
      sortToggleHandler: (sortBy: string, order: TSort) => {
         setSortParamsForm({ sortBy, order })
      },
      checkboxClickHandler: (
         e: SyntheticEvent,
         hasCheckboxesActiveState: boolean,
         checkedItemsIndex: number[]
      ) => {
         setRemoveButtonState(hasCheckboxesActiveState)
         setVisabilityChangeState(hasCheckboxesActiveState)
         setSelectedItems(checkedItemsIndex)
      },
      removeCheckedItemsHandler: () => {
         Requests.removeSelectedProducts()
      },
      removeClickHandler: (e: SyntheticEvent, index: number) => {
         dispatch(
            productActions.removeProduct({ _id: products.data[index]._id as string })
         )
      },
      updateVisabilityButtonClickHandler: () => {
         Requests.updateVisability()
         setIsClicked(true)
         setRemoveButtonState(false)
         setVisabilityChangeState(false)
      },
      editButtonClickHandler: (e: SyntheticEvent, index: number) => {
         dispatch(
            productActions.setFilters({
               query: searchForm.query,
               dateStart: searchForm.date_start,
               dateEnd: searchForm.date_end,
               categoryId: filterForm.value
            })
         )
         navigate(`/products/page=${page}/${products.data[index]._id as string}`)
      },
      checkedAll: (
         e: SyntheticEvent,
         hasCheckboxesActiveState: boolean,
         checkedItemsIndex: number[]
      ) => {
         setRemoveButtonState(hasCheckboxesActiveState)
         setVisabilityChangeState(hasCheckboxesActiveState)
         setSelectedItems(checkedItemsIndex)
      },
      resetFilters: () => {
         dispatch(
            productActions.setFilters({
               query: '',
               dateEnd: '',
               dateStart: '',
               categoryId: ''
            })
         )
         setSearchForm({
            query: '',
            date_start: '',
            date_end: ''
         })
         window.location.reload()
      }
   }

   const columns: TColumnTable[] = useMemo(
      () => [
         {
            Header: t('product.code'),
            accessor: 'barcode',
            width: 150,
            sortToggleHandler: Events.sortToggleHandler
         },
         {
            Header: t('photo'),
            accessor: 'photo'
         },
         {
            Header: t('label'),
            accessor: 'label',
            width: 250
         },
         {
            Header: t('createdAt'),
            accessor: 'createdAt',
            width: 200,
            sortToggleHandler: Events.sortToggleHandler
         },
         {
            Header: t('category'),
            accessor: 'category',
            width: 200,
            sortToggleHandler: Events.sortToggleHandler
         },
         {
            Header: t('total'),
            accessor: 'total',
            width: 200
         },
         {
            Header: t('price'),
            accessor: 'price',
            width: 200,
            sortToggleHandler: Events.sortToggleHandler
         },
         {
            Header: t('product.status'),
            accessor: 'sellStatus',
            width: 200,
            sortToggleHandler: Events.sortToggleHandler
         }
      ],
      []
   )

   const sections: TSection[] = useMemo(
      () => [
         {
            title: t('current'),
            active: true
         },
         {
            title: t('new.products'),
            onClickHandler: () => {
               navigate(`/products/newProducts`)
            }
         },
         {
            title: t('promotion.products'),
            onClickHandler: () => {
               navigate('/products/promotionProducts')
            }
         },
         {
            title: t('statistic'),
            onClickHandler: () => {
               navigate('/statistic')
            }
         },
         {
            title: t('changelog'),
            onClickHandler: () => {
               navigate('/changelog')
            }
         }
      ],
      []
   )

   const Requests = {
      getProducts: () => {
         dispatch(
            productActions.getProducts({
               limit: PAGE_LIMIT,
               page: page,
               lang: locale as ELocales,
               hidden: 'false',
               category: [filterForm.value],
               ...sortParamsForm,
               ...searchForm
            })
         )
      },
      getCategories: () => {
         dispatch(categoryActions.getCategories({ limit: 1000 }))
      },
      updateVisability: () => {
         const arr: string[] = []
         const tforms: {
            show: boolean
         }[] = []
         for (let i of selectedItems) {
            arr.push(products.data[i]._id)
            tforms.push({
               show: false
            })
         }
         dispatch(productActions.updateVisability({ ids: arr, tforms: tforms }))
         setSelectedItems([])
      },
      removeSelectedProducts: () => {
         const arr: string[] = []
         for (let i of selectedItems) {
            arr.push(products.data[i]._id)
         }
         dispatch(productActions.removeAllProducts({ ids: arr }))
         setSelectedItems([])
      }
   }

   useEffect(() => {
      Requests.getProducts()
      Requests.getCategories()
      setSearchParams({ ...searchParams, page: '' + (page + 1) })
   }, [page, filterForm, tableSortBy, searchForm])

   useEffect(() => {
      if (products?.meta) {
         if (page > Math.ceil(products?.meta.totalCount / PAGE_LIMIT)) {
            setPage(0)
         }
      }
   }, [products?.meta])

   useEffect(() => {
      if (productsResponse === RESPONSE.UPDATED_VISABILITY) {
         Requests.getProducts()
      }
      if (productsResponse === RESPONSE.REMOVED_ALL) {
         Requests.getProducts()
      }
      if (productsResponse === RESPONSE.REMOVED) {
         Requests.getProducts()
      }
   }, [productsResponse])

   // Set default page uri
   useEffect(() => {
      dispatch(productActions.setProduct({ product: null }))
      dispatch(
         productActions.setFilters({
            query: '',
            dateEnd: '',
            dateStart: '',
            categoryId: ''
         })
      )
      setPage(
         !isNaN(parseInt(searchParams.get('page') as string))
            ? parseInt(searchParams.get('page') as string) - 1
            : 0
      )
   }, [])

   return (
      <AdminPanelContainer
         Header={
            <Header
               buttonsList={
                  <>
                     <Button theme="blue" onClick={Events.postGoodsHandler}>
                        POST GOODS
                     </Button>

                     <Button theme="blue" onClick={Events.postAllGoodsHandler}>
                        POST ALL GOODS
                     </Button>

                     {changeVisabilityState && (
                        <Button
                           theme="green"
                           onClick={Events.updateVisabilityButtonClickHandler}>
                           {t('change.visability')}
                        </Button>
                     )}
                     {removeButtonState && (
                        <Button theme="red" onClick={Events.removeCheckedItemsHandler}>
                           {t('remove.all')}
                        </Button>
                     )}
                  </>
               }
               title={t('manage.products')}
            />
         }
         loading={productLoading}>
         <SubPagesBar sections={sections} />
         <FlexContainer direction="column">
            <HeaderWrapper style={{ justifyContent: 'flex-start', width: '99%' }}>
               <FiltersWrapper style={{ justifyContent: 'flex-start', width: '87%' }}>
                  <SearchBar
                     name="query"
                     placeholder={t('search')}
                     value={searchForm.query}
                     onChange={Events.onChangeHandler}
                     onSubmit={Events.onSubmitSearchHandler}
                  />
                  <DateInput
                     name="date_start"
                     value={searchForm.date_start}
                     onChange={Events.onChangeHandler}
                  />
                  <DateInput
                     name="date_end"
                     value={searchForm.date_end}
                     onChange={Events.onChangeHandler}
                  />
                  <DropDownList
                     width="20%"
                     placeholder={t('filter.by.category')}
                     options={categories?.data
                        ?.filter(
                           (item) =>
                              item.show &&
                              item.productsTotalCount &&
                              item.categoriesTotalCount == 0
                        )
                        ?.map((item) => ({
                           name: item.title,
                           value: item._id
                        }))}
                     value={filterForm.value}
                     onChange={Events.paginateDropDownChangeHandler}
                  />
                  <div onClick={Events.resetFilters}>
                     <Button style="transparant">{t('reset.filters')}</Button>
                  </div>
               </FiltersWrapper>
               <FlexContainer width="140px">
                  <div>{t('total.products')}:</div>
                  <div>{products?.meta?.totalCount}</div>
               </FlexContainer>
            </HeaderWrapper>

            {!isClicked && (
               <Table
                  columns={columns}
                  data={data}
                  sortBy={tableSortBy}
                  checkboxClickHandler={Events.checkboxClickHandler}
                  editClickHandler={Events.editButtonClickHandler}
                  removeClickHandler={Events.removeClickHandler}
                  headerCheckbox={Events.checkedAll}
                  editable
                  removeable
               />
            )}
            {isClicked && (
               <Table
                  columns={columns}
                  data={data}
                  sortBy={tableSortBy}
                  checkboxClickHandler={Events.checkboxClickHandler}
                  removeClickHandler={Events.removeClickHandler}
                  editClickHandler={Events.editButtonClickHandler}
                  headerCheckbox={Events.checkedAll}
                  editable
                  removeable
               />
            )}
         </FlexContainer>

         <Pagination
            page={page}
            pageCount={
               products?.meta ? Math.ceil(products?.meta.totalCount / PAGE_LIMIT) : 1
            }
            onPageChange={Events.onPageChangeHandler}
         />
      </AdminPanelContainer>
   )
}

export default IndexPage
